<template>
  <span data-testid="side-nav-container">
    <a
      class="navbar-hamburger navbar-hamburger-button"
      @click.prevent="toggleSideNav"
    >
      <px-icon icon="bars" size="lg" />
    </a>
    <!-- NextGen mobile top bar (shown < 1024px under the flag): a hamburger +
         POLLY wordmark that opens the full-screen drawer, matching the DS. -->
    <div class="ng-mobile-topbar">
      <a
        class="ng-mobile-hamburger navbar-hamburger-button"
        :aria-label="isVisible ? 'Close menu' : 'Open menu'"
        @click.prevent="toggleSideNav"
      >
        <px-icon :icon="isVisible ? 'xmark' : 'bars'" variant="light" />
      </a>
      <span class="ng-mobile-logo" aria-hidden="true">
        <polly-wordmark />
      </span>
    </div>
    <nav :class="navBarClasses">
      <div class="navbar-container">
        <div class="logo-row">
          <router-link class="polly-logo nav-button" exact-path to="/">
            <img
              alt="Polly logo"
              class="big-screen-logo"
              src="@shared/assets/images/polly-logo-black.svg"
            />
            <img
              alt="Polly logo"
              class="medium-screen-logo"
              src="@shared/assets/images/polly-mark-rgb-black.svg"
            />
            <!-- Exact Vue 3 design-system wordmark (shown only under the
                 classic_ui_nextgen_nav flag; see styles). -->
            <span class="ng-logo" aria-hidden="true">
              <polly-wordmark />
            </span>
            <!-- Exact DS "P/" mark for the narrow (tablet) rail. -->
            <span class="ng-logo-mark" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="24"
                fill="none"
              >
                <path
                  fill="#fff"
                  d="M3.601 14.203v5.598H.606V3.008h6.998c4.283 0 6.788 1.819 6.788 5.178s-2.505 5.178-6.788 5.178h-3.12zm4.003-3.596c2.547 0 3.75-.882 3.75-2.38s-1.259-2.42-3.75-2.42H3.601v4.786z"
                />
                <path fill="#01FFF0" d="m28.931 0 2.464 1.47L16 24h-3.541z" />
              </svg>
            </span>
            <a
              class="sm-screen-hamburger navbar-hamburger-button"
              @click.prevent="toggleSideNav"
            >
              <px-icon icon="bars" size="lg" />
            </a>
          </router-link>
        </div>
        <px-badge v-if="isSandbox" rectangle variant="new--outline" wrap>
          Sandbox Environment
        </px-badge>
        <side-nav-org-switcher />
        <div class="separation-line"></div>
        <div class="flex-nav-items">
          <!-- PE -->
          <side-nav-link-group
            v-if="orgUserPermissions.can_access_pe"
            large-text="PRICING ENGINE"
            mark-color="teal"
            text="PE"
            :to="{ name: 'pricingHome' }"
          >
            <side-nav-link
              v-if="orgUserPermissions.can_access_publish_pricing"
              icon="rectangle-list"
              navitem="pe_versions"
              text="Publish Pricing"
              :to="{ name: 'pricing' }"
            />
            <side-nav-link
              v-if="isInvestorStatusEnabled"
              icon="tower-cell"
              navitem="pe_investor-status"
              text="Investor Status"
              :href="investorStatusHref"
            />
            <side-nav-link
              v-if="
                orgPermissions.enable_custom_ratesheets &&
                isUserLockDeskOrHigher
              "
              icon="file-spreadsheet"
              navitem="rate_sheets"
              text="Rate Sheets"
              :to="{ name: 'rate-sheets' }"
            />
            <side-nav-link
              v-if="
                orgUserPermissions.can_access_mi_rate_card &&
                isUserLockDeskOrHigher
              "
              icon="umbrella"
              navitem="mi_versions"
              text="Publish MI"
              :to="{ name: 'mortgage-insurance-rate-card' }"
            />
            <side-nav-link
              v-if="orgUserPermissions.can_access_lock_desk"
              icon="lock"
              navitem="pe_lock_desk"
              text="Lock Desk"
              :to="{ name: 'lock-desk' }"
            />
            <side-nav-link
              v-if="orgUserPermissions.can_access_loan_pipeline"
              :badge-text="actionRequiredCount"
              icon="clipboard"
              navitem="pe_loan_pipeline"
              text="Loan Pipeline"
              :to="{ name: 'loans' }"
            >
              <template v-if="actionRequiredCount > 99" #badge-content>
                99
                <sup>+</sup>
              </template>
            </side-nav-link>
            <side-nav-link
              v-if="orgUserPermissions.can_access_loan_pipeline"
              icon="eye"
              navitem="pe_test_scenarios"
              :text="isNextGenNav ? 'Loan Scenario' : 'Loan Scenarios'"
              :to="{ name: 'loan-scenarios' }"
            />
            <side-nav-link
              v-if="isRateAlertManagmentEnabled"
              icon="bell"
              navitem="pe_rate_alerts"
              text="Rate Alerts"
              :to="{ name: 'rate-alerts' }"
            />
          </side-nav-link-group>
          <!-- Hedge — every Hedge page lives in the NextGen UI, so this is a
               primary tab that links out rather than a section with sub-items. -->
          <side-nav-link-group
            v-if="isHedgeUiEnabled"
            data-testid="side-nav-hedge-section"
            mark-color="green"
            text="HEDGE"
            :href="hedgeOverviewHref"
          />
          <!-- Sell -->
          <side-nav-link-group
            v-if="
              orgUserPermissions.can_access_exchange && orgPermissions.is_seller
            "
            data-testid="side-nav-sell-section"
            mark-color="yellow"
            :large-text="isNextGenNav ? 'SELL' : ''"
            :text="isNextGenNav ? 'S' : 'SELL'"
            :to="{ name: 'sellHome' }"
          >
            <div data-testid="side-nav-loan-manager">
              <side-nav-link
                icon="list-check"
                text="Loan Manager"
                :to="{ name: 'loanManager' }"
              />
            </div>
            <div data-testid="side-nav-uploads">
              <side-nav-link
                icon="upload"
                text="Uploads"
                :to="{ name: 'uploads' }"
              />
            </div>
            <div data-testid="side-nav-pipeline">
              <side-nav-link
                icon="clipboard"
                text="Pipeline"
                :to="{ name: 'pipeline' }"
              />
            </div>
            <div data-testid="side-nav-cart">
              <side-nav-link
                icon="cart-shopping"
                text="Cart"
                :to="{ name: 'commitmentCart' }"
              />
            </div>
            <div data-testid="side-nav-committed-loans">
              <side-nav-link
                icon="clipboard-check"
                text="Committed Loans"
                :to="{ name: 'committedLoans' }"
              />
            </div>
            <div data-testid="side-nav-committed-pools">
              <side-nav-link
                icon="box"
                text="Committed Pools"
                :to="{ name: 'committedPools' }"
              />
            </div>
          </side-nav-link-group>
          <!-- Buy -->
          <side-nav-link-group
            v-if="
              orgUserPermissions.can_access_exchange && orgPermissions.is_buyer
            "
            data-testid="side-nav-buy-section"
            mark-color="yellow"
            text="BUY"
            :to="{ name: 'buyHome' }"
          >
            <div data-testid="side-nav-bid-tape">
              <side-nav-link
                icon="download"
                text="Bid Tape"
                :to="{ name: 'bidTape' }"
              />
            </div>
            <div data-testid="side-nav-loan-discovery">
              <side-nav-link
                icon="magnifying-glass"
                text="Loan Discovery"
                :to="{ name: 'discovery' }"
              />
            </div>
            <div data-testid="side-nav-upload-bids">
              <side-nav-link
                icon="upload"
                text="Upload Bids"
                :to="{ name: 'bidUpload' }"
              />
            </div>
            <div data-testid="side-nav-active-bids">
              <side-nav-link
                icon="hourglass-half"
                text="Active Bids"
                :to="{ name: 'activeBids' }"
              />
            </div>
            <div data-testid="side-nav-buy-committed-loans">
              <side-nav-link
                icon="clipboard-check"
                text="Committed Loans"
                :to="{ name: 'buyCommittedLoans' }"
              />
            </div>
          </side-nav-link-group>
          <!-- Deliver -->
          <side-nav-link-group
            v-if="orgUserPermissions.can_access_delivery"
            text="DELIVER"
            :to="{ name: 'deliverHome' }"
          >
            <side-nav-link
              icon="box"
              navitem="deliver_ready"
              text="Ready"
              :to="{ name: 'ready' }"
            />
            <side-nav-link
              icon="paper-plane"
              navitem="deliver_in_progress"
              text="In Progress"
              :to="{ name: 'in-progress' }"
            />
            <side-nav-link
              icon="circle-check"
              navitem="deliver_delivered"
              text="Delivered"
              :to="{ name: 'delivered' }"
              tooltip="Delivered"
            ></side-nav-link>
          </side-nav-link-group>
          <!-- Analytics -->
          <side-nav-link-group
            v-if="orgUserPermissions.can_access_analytics"
            mark-color="pink"
            text="ANALYTICS"
            :to="{ name: 'analyticsHome' }"
          >
            <side-nav-link
              icon="display-chart-up"
              text="Analytics"
              :to="{ name: 'analyticsHome' }"
            />
          </side-nav-link-group>
          <!-- A primary tab beside Analytics, not a child of it. The per-dashboard sub-items the NextGen
               nav lists are a classic-side follow-up (APP-33183). -->
          <side-nav-link-group
            v-if="isAnalyticsDashboardsUiEnabled"
            mark-color="pink"
            text="ANALYTICS 2"
            :href="analyticsDashboardsHref"
          />
          <!-- Tools -->
          <side-nav-link-group
            v-if="orgUserPermissions.can_access_lock_updater"
            text="TOOLS"
            :to="{ name: 'toolsHome' }"
          >
            <side-nav-link
              icon="pen-to-square"
              text="Lock Updater"
              :to="{ name: 'lock_updater' }"
            />
          </side-nav-link-group>
          <!-- Dev tools -->
          <side-nav-link-group
            v-if="orgUserPermissions.can_access_seed_data"
            text="DEV"
            :to="{ name: 'devtoolsHome' }"
          >
            <side-nav-link
              icon="face-glasses"
              text="Seed Test Data"
              :to="{ name: 'seed_data_scenario' }"
            />
          </side-nav-link-group>
        </div>
        <div class="separation-line"></div>
        <a
          v-if="showNewUiLink"
          class="nav-button ng-footer-item"
          :class="{ 'disabled-link': isNewUiLinkDisabled }"
          href="#"
          @click.prevent="navigateToNewUi"
        >
          <div class="nav-icon">
            <px-icon icon="shuffle" inline />
          </div>
          <div class="nav-text">Next-gen UI</div>
        </a>
        <side-nav-link
          v-if="showNotifications"
          class="ng-footer-item"
          icon="bell"
          text="Notifications"
          :to="{ name: 'notification-preferences' }"
        ></side-nav-link>
        <side-nav-link-group
          class="ng-footer-profile"
          :emphasize-text="false"
          icon="circle-user"
          show-tooltip
          :text="profileHeaderText"
          :to="{ name: 'account_profile' }"
        >
          <template #icon>
            <span v-if="isNextGenNav" class="ng-avatar">
              {{ userInitials }}
              <span class="ng-avatar__dot"></span>
            </span>
            <px-icon v-else icon="circle-user" />
          </template>
          <side-nav-link
            v-if="isNextGenNav"
            class="nested"
            icon="circle-user"
            text="Profile"
            :to="{ name: 'account_profile' }"
          />
          <side-nav-link
            class="nested"
            icon="users-viewfinder"
            text="Membership"
            :to="{ name: 'account_membership' }"
          />
          <span
            v-if="!orgUserPermissions.is_sso_enabled"
            @click="navigateTo('/accounts/password/change/')"
          >
            <side-nav-link
              class="nested"
              icon="shield-keyhole"
              text="Change Password"
              to="/accounts/password/change/"
            />
          </span>
          <span @click="navigateTo('/accounts/logout/')">
            <side-nav-link
              class="nested"
              icon="arrow-right-from-bracket"
              text="Sign Out"
              to="/accounts/logout/"
            ></side-nav-link>
          </span>
        </side-nav-link-group>
        <side-nav-link
          v-if="orgUserPermissions.is_admin"
          class="ng-footer-admin ng-footer-item"
          :exact-path-match="true"
          icon="cog"
          text="Admin"
          :to="{ name: 'useradmin' }"
        />
      </div>
      <button :class="shadowClasses" @click.exact="toggleSideNav"></button>
    </nav>
  </span>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  PRICER_UI_REDESIGN_FRONTEND,
  ENABLE_INVESTOR_DASHBOARD,
  CLASSIC_UI_NEXTGEN_NAV,
  ANALYTICS_DASHBOARDS_UI,
  HEDGE_UI,
} from '@shared/constants/flags';

import { pollWhenActive } from '@shared/utils/polling.js';
// Exact Vue 3 nav font (InterVariable), self-hosted from the design system.
import '@shared/styles/inter-variable.css';
import SideNavOrgSwitcher from './SideNavOrgSwitcher.vue';
import SideNavLinkGroup from './SideNavLinkGroup.vue';
import SideNavLink from './SideNavLink.vue';
import PollyWordmark from './PollyWordmark.vue';
import { setCookie } from '@shared/utils/cookies.js';
import {
  newAppBaseUrl,
  replaceUrlParameter,
  resolveNewAppUrl,
} from '@shared/utils/routing.js';

const ESCAPE_CODE = 'Escape';

const HEDGE_OVERVIEW_PATH = '/hedge/overview';

export default {
  components: {
    SideNavLink,
    SideNavLinkGroup,
    SideNavOrgSwitcher,
    PollyWordmark,
  },
  props: {},
  data() {
    return {
      isVisible: false,
    };
  },
  computed: {
    ...mapGetters({
      sandboxType: 'core/sandboxType',
      orgUserPermissions: 'core/orgUserPermissions',
      orgPermissions: 'core/orgPermissions',
      userInfo: 'core/userInfo',
      actionRequiredCount: 'loanPipeline/actionRequiredCount',
      isUserLockDeskOrHigher: 'core/isUserLockDeskOrHigher',
      isRateAlertManagmentEnabled: 'core/isRateAlertManagementEnabled',
      isFlagActive: 'core/isFlagActive',
      currentUser: 'members/currentUser',
      currentOrganization: 'organizations/organization',
    }),
    hideSideNav() {
      return this.$route.params?.hideSideNav ?? false;
    },
    profileHeaderText() {
      return this.userInfo?.full_name ?? 'Profile';
    },
    navBarClasses() {
      return {
        'polly-navbar': true,
        'nav-slide-in': this.isVisible,
        'nav-slide-out': !this.isVisible,
      };
    },
    shadowClasses() {
      return {
        'navbar-shadow': true,
        'shadow-slide-in': this.isVisible,
        'shadow-slide-out': !this.isVisible,
      };
    },
    showNewUiLink() {
      const canSwitch =
        this.userInfo?.is_staff &&
        this.isFlagActive(PRICER_UI_REDESIGN_FRONTEND);
      // NextGen restyle: only surface the switcher on pages that actually have
      // a NextGen (Vue 3) equivalent, rather than showing it disabled/faded.
      if (this.isNextGenNav) {
        return canSwitch && this.hasReachableNewAppPage;
      }
      return canSwitch;
    },
    isNewUiLinkDisabled() {
      return !this.hasReachableNewAppPage;
    },
    // A route sets newAppFlag when its NextGen page exists only for orgs with that flag.
    hasReachableNewAppPage() {
      const { newAppPath, newAppFlag } = this.$route.meta ?? {};
      return !!newAppPath && (!newAppFlag || this.isFlagActive(newAppFlag));
    },
    showNotifications() {
      return this.orgUserPermissions?.can_access_notifications;
    },
    hasAutoTriggeredRepriceEnabled() {
      return this.orgPermissions?.has_auto_triggered_reprice_enabled;
    },
    isSandbox() {
      return this.sandboxType === 'SANDBOX';
    },
    isInvestorStatusEnabled() {
      return (
        this.isUserLockDeskOrHigher &&
        this.isFlagActive(ENABLE_INVESTOR_DASHBOARD)
      );
    },
    investorStatusHref() {
      return resolveNewAppUrl('/pe/investor-status');
    },
    isAnalyticsDashboardsUiEnabled() {
      return this.isFlagActive(ANALYTICS_DASHBOARDS_UI);
    },
    analyticsDashboardsHref() {
      return resolveNewAppUrl('/analytics-dashboards/lock-volume');
    },
    isHedgeUiEnabled() {
      return this.isFlagActive(HEDGE_UI);
    },
    hedgeOverviewHref() {
      return resolveNewAppUrl(HEDGE_OVERVIEW_PATH);
    },
    isNextGenNav() {
      return this.isFlagActive(CLASSIC_UI_NEXTGEN_NAV);
    },
    userInitials() {
      const name = this.userInfo?.full_name || '';
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('');
    },
  },

  watch: {
    // Collapse the mobile drawer after navigating to another route.
    $route() {
      if (this.isVisible) {
        this.isVisible = false;
      }
    },
  },
  mounted() {
    document.addEventListener('keyup', this.handleEsc);

    // Flag-gated NextGen design-system restyle: toggle a body class the styles
    // key off of (also lets us shift the main content margin).
    if (this.isNextGenNav) {
      document.body.classList.add('nextgen-nav');
    }

    if (this.showNotifications) {
      pollWhenActive(() => this.fetchUnreadCount(), 60000, 0.1);
    }

    if (this.hasAutoTriggeredRepriceEnabled) {
      pollWhenActive(() => this.fetchActionRequiredCountPolling(), 30000, 0.1);
    }
  },
  destroyed() {
    document.removeEventListener('keyup', this.handleEsc);
    document.body.classList.remove('nextgen-nav');
  },
  methods: {
    ...mapActions({
      fetchUnreadCount: 'notifications/fetchUnreadCount',
      fetchActionRequiredCountPolling:
        'loanPipeline/fetchActionRequiredCountPolling',
    }),
    toggleSideNav() {
      this.isVisible = !this.isVisible;
    },
    handleEsc($event) {
      if (this.isVisible && $event.code === ESCAPE_CODE) this.isVisible = false;
    },
    navigateTo(path) {
      location.href = path;
    },
    navigateToNewUi() {
      if (this.isNewUiLinkDisabled) {
        return;
      }

      setCookie(
        'defaultNewUI',
        {
          orgId: this.currentOrganization?.id,
          userId: this.currentUser?.id,
          state: true,
        },
        {
          expiresInMs: 24 * 60 * 60 * 1000,
        },
      );

      // Target the NextGen path, not the Classic one: every migrated section renames segments
      // (/sell/loan_manager -> /exchange/loan-manager, company/users -> company/members), so
      // forwarding $route.path lands on a URL the NextGen UI does not serve. The
      // isNewUiLinkDisabled guard above is what makes newAppPath safe to read directly.
      const targetPath = replaceUrlParameter(
        this.$route.meta.newAppPath,
        this.$route.params,
      );

      location.href = `${newAppBaseUrl()}${targetPath}${location.search}`;
    },
  },
};
</script>

<style lang="scss">
@import '@shared/styles/variables';

$active-color: $polly-light-blue;
$inactive-color: $polly-dark-blue;

/* ─────────────────────────────────────────────────────────────────────────
   NextGen design-system look — gated by the `classic_ui_nextgen_nav` flag,
   which makes the component add `nextgen-nav` to <body>. Restyles the existing
   Classic SideNav to closely match the Vue 3 design-system nav (dark theme,
   240px, DS accent colors). Uses real DS token values:
   nav bg = --colors-nav-mid #333 / darkest #000; accents teal #01fff0,
   yellow #ffd601, pink #ff54d9.
   ───────────────────────────────────────────────────────────────────────── */
// Exact DS nav background = --colors-nav-mid (#333333).
$ng-bg: #333333;
$ng-text: #e8e8e8;
$ng-icon: #9a9a9a;
$ng-accent: #01fff0;
$ng-border: rgba(255, 255, 255, 0.1);
// Per-section "/" mark colors (DS brand accents).
$ng-mark-teal: $ng-accent;
$ng-mark-yellow: #ffd601;
$ng-mark-pink: #ff54d9;
$ng-mark-green: #74c365;

// Exact DS wordmark + section marks are hidden in normal Classic mode
// (only shown under the flag). The compact "P/" mark is hidden until the
// narrow tablet rail (see the medium media query).
.ng-logo,
.ng-logo-mark,
.ng-section-mark,
.ng-mobile-topbar {
  display: none;
}

body.nextgen-nav {
  .main > .mai-wrapper {
    margin-left: 240px;
  }

  .navbar-shadow {
    left: 240px;
  }

  .polly-navbar {
    width: 240px;
    background: $ng-bg;
    border: none;
    box-shadow: none;
    color: $ng-text;
    // Exact DS font family.
    font-family: 'InterVariable', sans-serif;

    & > .navbar-container {
      width: 240px;
      background: $ng-bg;
      // Match DS: content padding-inline = --spacing-md (16px); 8px item gap;
      // 12px (--spacing-sm) below the footer, like the DS footer's padding-block.
      padding: 0 16px 12px;
      gap: 8px;
    }

    // Logo: centered with --spacing-sm (12px) padding, like the DS nav.
    // 16px (--spacing-base) gap below, matching the DS aside gap.
    .logo-row {
      display: flex;
      justify-content: center;
      padding: 12px;
    }

    // Org row spacing: 3px top/bottom margin around the 54px org link (12px
    // button padding) keeps the org section at the 60px that ui-application's
    // nav renders (measured, not a DS token).
    .user-orgs-accordion {
      margin-top: 3px;
      margin-bottom: 3px;
    }

    // 8px gaps between sections and (when open) their items.
    .flex-nav-items,
    .parent-item {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    // Accordion behavior like the DS: only the active section is expanded;
    // all other sections collapse to just their header.
    .sub-items {
      display: none;
    }
    .active-parent-item .sub-items {
      display: flex;
      flex-direction: column;
      // DS open group content: --spacing-xs (8px) between rows, and
      // --spacing-md (16px) below the last one.
      gap: 8px;
      padding-bottom: 16px;
    }
    // A collapsed/inactive section stays collapsed even while its header is
    // focused — overrides the Classic `:focus-within` reveal (higher
    // specificity via the extra `body.nextgen-nav` element).
    .parent-item:not(.active-parent-item) > .sub-items {
      display: none;
    }

    // Sub-menu (leaf) items: DS row height (--button-size-md, 32px; the 12px
    // padding overlaps the centered label, as in the DS) and 12px text (org
    // name / section headers / footer stay at 14px).
    .sub-items .nav-button {
      height: 32px;
    }
    .sub-items .nav-button .nav-text {
      font-size: 12px;
    }

    // Active section sits in a black rounded card
    // (DS --colors-background-uncommon-nav-section-active = nav-darkest #000).
    .active-parent-item {
      background: #000000;
      // DS --border-radius-md.
      border-radius: 24px;
      // No padding so the active-item highlight can span to the card edges.
      padding: 0;
      // Clip children (e.g. the header hover) to the rounded card so nothing
      // bleeds past its corners.
      overflow: hidden;
    }
    .polly-logo.nav-button {
      justify-content: center;
      width: 100%;
      padding: 0;

      &:hover {
        background: transparent;
      }
    }
    // The logo links to "/", so on the root route it becomes router-link-active
    // and would pick up the active-link styling — suppress it (no teal border).
    .polly-logo.router-link-active {
      background: transparent;
      border-right: none;
      border-radius: 0;
    }

    // Brand-colored "/" section mark (approximates the DS polly-slash icon).
    // The SVG uses fill:currentColor, so the modifier classes set the color.
    .ng-section-mark {
      display: inline-flex;
      align-items: center;

      svg {
        display: block;
        // DS slash icon is 20×20, no right margin.
        width: 20px;
        height: 20px;
      }
    }
    .ng-section-mark--teal {
      color: $ng-mark-teal;
    }
    .ng-section-mark--yellow {
      color: $ng-mark-yellow;
    }
    .ng-section-mark--pink {
      color: $ng-mark-pink;
    }
    .ng-section-mark--green {
      color: $ng-mark-green;
    }

    // Use the exact Vue 3 wordmark SVG; hide the Classic raster logos.
    .big-screen-logo,
    .medium-screen-logo {
      display: none;
    }
    .ng-logo {
      display: inline-flex;
      align-items: center;
    }

    .separation-line {
      border-top: 1px solid $ng-border;
      background: transparent;
    }

    // Links + footer items (all use .nav-button). DS item size = --font-size-md
    // (0.875rem/14px), padding = --spacing-sm (12px).
    .nav-button {
      border-radius: 8px;
      padding: 12px;
      box-sizing: border-box;

      .nav-text {
        // DS nav label text is white (--colors-text-icon-light).
        color: #ffffff;
        // Explicit px — Classic's rem base is 13px, DS renders at 14px.
        font-size: 14px;
        // DS nav buttons are --font-weight-medium (500).
        font-weight: 500;
        // DS button label line-height and tracking (measured in ui-application).
        line-height: 1.3;
        letter-spacing: 0.002em;
      }
      .nav-icon {
        color: $ng-icon;
        // PButton size lg icon (--icon-size-sm), used by DS section headers and
        // org rows; footer items override it to 12px below.
        font-size: 16px;
        width: 16px;
        height: 16px;
        margin-right: 12px;
      }

      &:hover {
        background: #000000;

        .nav-text,
        .nav-icon {
          color: #ffffff;
        }
      }
    }

    // Solid black fully rounded pill on hover (DS --border-radius-xl), shared by
    // the collapsed/inactive section headers AND the footer items (Next-gen UI
    // switcher, Notifications, Admin, and the user/account row).
    .parent-item:not(.active-parent-item) .nav-button:hover,
    .ng-footer-item.nav-button:hover,
    .ng-footer-item .nav-button:hover {
      background: #000000;
      border-radius: 99px;
    }
    // Footer items rest as fully rounded pills too.
    .ng-footer-item.nav-button,
    .ng-footer-item .nav-button {
      border-radius: 99px;
    }

    // Section header (main menu item): DS row height (--button-size-lg, 40px)
    // and fully rounded (--border-radius-xl).
    .ng-parent-header {
      height: 40px;
      border-radius: 99px;
    }
    // Section headers (PRICING ENGINE, SELL, …) — match DS 14px medium.
    // (Higher specificity so it beats the generic .nav-button .nav-text size.)
    .nav-button .nav-text.nav-text-header,
    .nav-button .nav-text.big-screen-text {
      color: #ffffff;
      font-weight: 500;
      font-size: 14px;
      line-height: 1.3;
      letter-spacing: 0.002em;
      text-transform: none;
    }
    // DS raises a collapsed/inactive group header to semibold on hover
    // (`.polly-navigation-group__collapsed-button:hover`); the expanded header
    // stays at 500.
    .parent-item:not(.active-parent-item)
      .ng-parent-header:hover
      .nav-text-header,
    .parent-item:not(.active-parent-item)
      .ng-parent-header:hover
      .big-screen-text {
      font-weight: 600;
    }

    // All nav icons use FA 6 Pro Light (weight 300) to match the DS `fa-light`.
    .px-icon {
      font-weight: 300;
    }

    // Active item
    .router-link-active {
      // DS subsection-active = nav-mid (#333), full-width, slight left rounding,
      // 3px accent border flush to the card's right edge.
      background: #333333;
      border-radius: 4px 0 0 4px;
      border-right: 3px solid $ng-accent;

      .nav-text {
        color: #ffffff;
        font-weight: 500;
      }
      .nav-icon {
        color: $ng-accent;
      }

      &::after {
        display: none;
      }
    }

    // Active sub-link keeps its active style on hover (no lighter hover swap).
    .active-parent-item .router-link-active:hover {
      background: #333333;
    }

    // The active sub-item's accent border matches its section's slash color
    // (teal is the default via $ng-accent; override for yellow/pink sections).
    .ng-section--yellow .router-link-active {
      border-right-color: $ng-mark-yellow;
    }
    .ng-section--pink .router-link-active {
      border-right-color: $ng-mark-pink;
    }

    // Group/section headers are toggles, not pages — never render the active
    // link styling (no #333 fill, teal accent, or right border).
    .ng-parent-header.router-link-active {
      background: transparent;
      border-right: none;
      border-radius: 99px;

      .nav-icon {
        color: $ng-icon;
      }
    }

    // Active ungrouped (footer) item: just the darker style — no teal accent,
    // no right-side border, no left rounding (that treatment is for in-card
    // sub-links only). Keeps the resting 99px pill radius; hovering it stays
    // the same.
    .ng-footer-item.router-link-active,
    .ng-footer-item .router-link-active {
      background: #000000;
      border-right: none;

      .nav-icon {
        color: #ffffff;
      }
    }

    // Hide the Classic divider lines (the DS nav has none).
    .separation-line {
      display: none;
    }

    // DS leaf items are text-only — hide per-item icons inside sections and
    // indent the labels to sit under the section heading. The 8px margin plus
    // the 8px `.sub-items` padding from Classic give the DS 16px inset.
    .sub-items .nav-icon {
      display: none;
    }
    .sub-items .nav-button {
      margin-left: 8px;
      // DS leaf rows round only their left corners (the active one carries the
      // accent border on the right).
      border-radius: 4px 0 0 4px;
    }
    // DS leaf rows go semibold on hover.
    .sub-items .nav-button:not(.router-link-active):hover .nav-text {
      font-weight: 600;
    }
    // DS active leaf row is bold.
    .sub-items .router-link-active .nav-text {
      font-weight: 700;
    }

    // Footer items (Next-gen UI switcher, Notifications) — DS row height
    // (--button-size-md, 32px), 12px icon (--icon-size-xxs) in a 15px box
    // (measured in ui-application at ui-components 3.1.6), 8px icon gap,
    // regular weight label. `.ng-footer-item` sits on the nav-button
    // for the switcher and on the SideNavLink wrapper for Notifications, so
    // match both. Admin is a PNavigationGroup size lg in the DS and keeps the
    // section-header values (40px row, 16px icon, weight 500).
    .ng-footer-item:not(.ng-footer-admin).nav-button,
    .ng-footer-item:not(.ng-footer-admin) .nav-button {
      height: 32px;
    }
    .ng-footer-item .nav-icon {
      // Footer icons, Admin's included, measure white in ui-application at
      // ui-components 3.1.6, not the muted section-icon grey.
      color: #ffffff;
    }
    .ng-footer-item:not(.ng-footer-admin) .nav-icon {
      font-size: 12px;
      width: 15px;
      height: 15px;
      margin-right: 8px;
    }
    .ng-footer-item:not(.ng-footer-admin) .nav-text {
      font-weight: 400;
    }
    .ng-footer-admin.nav-button,
    .ng-footer-admin .nav-button {
      height: 40px;
    }
    // The switcher is itself a direct flex child of .navbar-container (column),
    // so stop it shrinking below its height.
    .ng-footer-item.nav-button {
      flex-shrink: 0;
    }

    // Match DS footer order: Admin above the user/account row.
    .ng-footer-admin {
      order: 1;
    }
    .ng-footer-profile {
      order: 2;
    }

    // User avatar (initials + green online dot), matching ui-application's
    // AccountSelect: a 54px row (12px padding around a 30px avatar), 12px
    // avatar-to-name gap, and a 16px regular-weight name (computed values of
    // `.account-select__user-name`).
    .ng-footer-profile .ng-parent-header {
      height: 54px;
    }
    .ng-footer-profile .nav-icon {
      width: 30px;
      height: 30px;
      margin-right: 12px;
    }
    .ng-footer-profile .nav-text {
      font-size: 16px;
      font-weight: 400;
      line-height: 1.3;
      // AccountSelect's name is not a PButton label, so no tracking.
      letter-spacing: normal;
    }
    // Expanded-nav hover pill only; the medium rail resets it to square below.
    .ng-footer-profile .nav-button:hover {
      background: #000000;
      border-radius: 99px;
    }
    .ng-avatar {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      // PAvatar lg = 28px disc + 1px border (30px overall). Initials are
      // --font-size-xs (11px), set by ui-application's AccountSelect, in
      // --colors-text (#595959).
      box-sizing: border-box;
      width: 30px;
      height: 30px;
      border-radius: 999px;
      // DS avatar disc and border (--colors-primary tints).
      background: #e7ecfc;
      border: 1px solid #cfd9f9;
      color: #595959;
      font-size: 11px;
      font-weight: 500;
      line-height: 1.3;
      letter-spacing: 0.002em;
    }
    // PAvatar status dot: 8px content box plus a 1px ring (10px overall),
    // centered 24.89px in from the avatar's top-left corner (85.36% of the
    // 28px disc, past its 1px border). The 23.89px offsets are measured from
    // ui-application at ui-components 3.1.6.
    .ng-avatar__dot {
      position: absolute;
      left: 23.89px;
      top: 23.89px;
      width: 8px;
      height: 8px;
      box-sizing: content-box;
      border-radius: 999px;
      // DS online green (--colors-bespoke-lime #61C554).
      background: #61c554;
      border: 1px solid #333333;
      transform: translate(-50%, -50%);
    }
  }
}

/* ── NextGen medium / tablet rail (matches the DS narrow labeled rail at this
      width, NOT Classic's 82px icon-only rail). Scoped to 1024–1399px so the
      ≥1400px view is unchanged and < 1024px gets the mobile drawer. The DS rail
      ends at 1279px while Classic's runs to 1399px (known difference). ── */
@media (max-width: 1399px) and (min-width: 1024px) {
  body.nextgen-nav {
    .main > .mai-wrapper {
      margin-left: 88px;
    }
    .navbar-shadow {
      left: 88px;
    }

    .polly-navbar {
      width: 88px;

      & > .navbar-container {
        width: 88px;
        // Narrow rail: no side padding; items span the full width. 12px below
        // the footer, like the DS footer's padding-block.
        padding: 0 0 12px;
      }

      // Compact "P/" mark instead of the full wordmark. No bottom padding — the
      // 20px logo→org gap is provided by the org's margin + container gap, so an
      // extra 12px here would push the whole column down out of alignment.
      .logo-row {
        padding: 12px 0 0;
      }
      .ng-logo {
        display: none;
      }
      .ng-logo-mark {
        display: inline-flex;
      }

      // Org: name only (no icon), centered and allowed to wrap to two lines
      // (like the DS). Force the label visible — Classic's medium rail hides
      // `.nav-icon + .nav-text`.
      // Match the DS org spacing at this width: 20px below the logo, 8px above
      // the first section (container gap is 8px, so top 12 + gap 8 = 20).
      .user-orgs-accordion {
        margin-top: 12px;
        margin-bottom: 0;
      }
      .ng-org-link {
        flex-direction: column;
        justify-content: center;
        height: 66px;
        padding: 4px;

        .nav-icon {
          display: none;
        }
        .nav-text {
          display: block !important;
          width: 100%;
          // DS org name = --font-size-md (14px) / line-height 18.2.
          font-size: 14px;
          line-height: 1.3;
          text-align: center;
          white-space: normal;
        }
      }

      // Section header: abbreviated label + slash, centered on one row. Same
      // 40px DS row height as the wide view; the DS's 12px side padding is kept
      // at 4px so Classic's longer "Hedge" label still fits in the 88px rail.
      .ng-parent-header {
        justify-content: center;
        padding: 12px 4px;
      }
      .ng-section-mark {
        justify-content: center;
      }
      .nav-button .nav-text.medium-screen-text {
        display: block;
        text-align: center;
        font-size: 14px;
        font-weight: 500;
      }

      // Active section: full-width black region — drop the rounded card.
      .active-parent-item {
        border-radius: 0;
        padding: 0;
      }
      // Menu-item backgrounds (hover + active-item highlight) are sharp-cornered
      // full-width boxes in the narrow rail — not the wide-view rounded pill.
      .parent-item:not(.active-parent-item) .nav-button:hover,
      .ng-footer-item.nav-button:hover,
      .ng-footer-item .nav-button:hover,
      .ng-footer-profile .nav-button:hover,
      .sub-items .router-link-active {
        border-radius: 0;
      }
      .active-parent-item .sub-items {
        // Flush to the left edge so item backgrounds span the full rail width.
        padding-left: 0;
      }

      // Sub-items: reveal the wrapped label (Classic medium hides it), drop the
      // icon and center. The wide view's 32px DS row height becomes a minimum so
      // Classic labels that wrap past two lines in the narrow rail don't clip.
      // Hovered inactive leaves are square, as in the DS.
      .sub-items .nav-button {
        height: auto;
        min-height: 32px;
        // The wide view's 12px padding overlaps its fixed-height label; with a
        // growing row it would add 24px to every wrapped label.
        padding-top: 0;
        padding-bottom: 0;
        margin-left: 0;
        border-radius: 0;
        justify-content: center;
        text-align: center;
      }
      .sub-items .nav-button > .nav-icon + .nav-text,
      .sub-items .nav-button .nav-text {
        display: block !important;
        font-size: 12px;
        line-height: 1.3;
        text-align: center;
        white-space: normal;
      }
      // Active sub-item highlight spans the full rail width.
      .sub-items .router-link-active {
        border-radius: 0;
      }

      // Footer stays icon-only (Classic medium hides the labels); center it and
      // drop the icon's label gap so the glyph sits on the rail's center line,
      // like the DS. Row height is the wide view's 32px.
      // The DS sets `.polly-navigation-aside__footer { padding-inline: 0 }` at
      // 1024-1279px, but ui-application's AppNavigation.vue restores
      // `padding-inline: var(--spacing-md)`, so its footer buttons measure 56px
      // wide and square at 1100px (ui-components 3.1.6).
      .ng-footer-item,
      .ng-footer-profile {
        margin-left: 16px;
        margin-right: 16px;
      }
      .ng-footer-item.nav-button,
      .ng-footer-item .nav-button,
      .ng-footer-profile .nav-button {
        justify-content: center;
        border-radius: 0;
      }
      .ng-footer-item .nav-icon,
      .ng-footer-profile .nav-icon {
        margin-right: 0;
      }
    }
  }
}

/* ── NextGen mobile (< 1024px): a fixed top bar (hamburger + wordmark) that
      opens a full-screen drawer showing the full nav, matching the DS. ── */
@media (max-width: 1023px) {
  body.nextgen-nav {
    // Top bar: hamburger + POLLY wordmark. Sits ABOVE the drawer (z 110 > 100)
    // and doubles as the open drawer's header — its icon swaps hamburger <-> X
    // on open, so the wordmark never moves.
    .ng-mobile-topbar {
      display: flex;
      align-items: center;
      // DS top bar: --spacing-xs (8px) between controls, 16px side padding.
      gap: 8px;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 64px;
      padding: 0 16px;
      background: $ng-bg;
      z-index: 110;
    }
    .ng-mobile-hamburger {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      cursor: pointer;
      // DS md button icon = --polly-button-icon-font-size 1.25rem (20px at the DS
      // 16px base). Use px — Classic's rem base is 13px.
      font-size: 20px;
    }
    // The X glyph reads visually smaller than the bars at the same font-size, so
    // bump the close icon up a touch to balance them.
    .ng-mobile-hamburger .fa-xmark {
      font-size: 24px;
    }
    .ng-mobile-logo svg {
      display: block;
    }
    // Hide the Classic floating hamburger button.
    .navbar-hamburger {
      display: none !important;
    }

    // Main content sits below the top bar (no sidebar gutter).
    .main > .mai-wrapper {
      margin-left: 0;
      margin-top: 64px;
    }

    // The nav becomes a full-screen drawer sliding in from the left. Uses a CSS
    // transform (overriding Classic's `left`-based phone animation) driven by
    // the existing nav-slide-in / nav-slide-out classes.
    .polly-navbar {
      width: 100vw;
      left: 0;
      transition: transform 0.3s ease;
      animation: none !important;
      z-index: 100;

      // Slide DOWN from the top (matching the DS), not in from the side.
      &.nav-slide-out {
        left: 0;
        transform: translateY(-100%);
      }
      &.nav-slide-in {
        left: 0;
        transform: translateY(0);
      }

      & > .navbar-container {
        width: 100%;
      }
    }

    // The drawer uses the fixed top bar as its header, so hide the drawer's own
    // logo row and its inline hamburger/close control entirely. Scoped under
    // .polly-navbar to beat the base `.polly-navbar .logo-row { display: flex }`.
    .polly-navbar .logo-row {
      display: none;
    }
    .sm-screen-hamburger {
      display: none;
    }

    // Polly AI (chat) button sits top-right on mobile, matching the DS — it's a
    // global (unscoped) element under body.nextgen-nav.
    .chat-widget {
      top: 8px;
      bottom: auto;
      right: 16px;
    }

    // No separate dimming overlay — the drawer is full-screen.
    .navbar-shadow {
      display: none !important;
    }

    // The drawer shows the FULL nav (labels, icons, left-aligned) — undo
    // Classic's medium (776–1399px) icon-rail collapse that would otherwise
    // apply below 1024px and blank out the labels.
    .polly-navbar > .navbar-container {
      // 64px top clears the fixed top bar that serves as the drawer header.
      padding: 64px 16px 16px;
    }
    .nav-button {
      justify-content: flex-start;
    }
    .nav-button > .nav-icon + .nav-text {
      display: block !important;
    }
    .nav-icon {
      margin-right: 12px !important;
    }
    .big-screen-text {
      display: block;
    }
    .medium-screen-text {
      display: none;
    }
  }
}

.main {
  > .mai-wrapper {
    margin-left: 194px;
  }
}

.nav-button {
  display: flex;
  align-items: center;
  padding: 8px 0 8px 0;

  &:hover {
    .nav-icon {
      color: $active-color;
    }

    .nav-text {
      color: $active-color;
    }
  }

  &.nested {
    padding: 4px 0 4px 0;
  }

  & .nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    width: 20px;
    height: 20px;
    margin-right: 16px;
    color: $inactive-color;
    position: relative;
    transition: color 100ms ease-in-out;

    .icon__badge {
      position: absolute;
      font-size: 0.7rem;
      left: 10px;
      top: -6px;

      .super-text {
        margin-left: -2px;
      }
    }
  }

  & > .nav-icon svg.px-icon {
    width: 20px;
    height: 20px;
  }

  & > .nav-text {
    font-size: 12px;
    line-height: 15px;
    color: $inactive-color;
    white-space: normal;
    transition: color 100ms ease-in-out;
  }

  & > .nav-text-header {
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;
  }

  &.disabled-link {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      .nav-icon,
      .nav-text {
        color: $inactive-color;
      }
    }
  }
}

.nested {
  .nav-button .nav-icon {
    font-size: 14px;

    .px-icon {
      overflow: visible;
      height: 14px;
      width: 14px;
    }

    &:before {
      content: '\2022';
      margin-right: 8px;
    }
  }
}

.navbar-shadow {
  top: 0;
  left: 194px;
  height: 100vh;
  width: 100%;
  position: fixed;
  appearance: none;
  border: none;
  z-index: 99;
  background-color: rgba(0, 0, 0, 0.6);
}

.polly-navbar {
  background: #ffffff;
  border: 1px solid #f2f2f2;
  bottom: 0;
  box-shadow:
    inset 190px 0 5px 0 white,
    inset 0 0 6px 1px rgba(0, 0, 0, 0.25);
  box-sizing: border-box;
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  z-index: 100;

  .polly-logo:focus-visible {
    outline: 2px solid $polly-light-blue;
    outline-offset: 2px;
    border-radius: 5px;
  }

  a.nav-button {
    &:focus-visible {
      text-decoration: none;

      .nav-text,
      .nav-icon {
        color: $active-color;
      }

      .nav-text {
        text-decoration: underline;
      }
    }
  }

  & > nav {
    height: 100%;
  }

  & > .navbar-container {
    width: 194px;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    overflow-y: auto;
    overflow-x: hidden;

    & > .flex-nav-items {
      flex: 1;
    }
  }

  .router-link-active {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: -1rem;
      border-right: 2px solid $active-color;
    }
  }

  .router-link-active.nav-button > .nav-icon,
  .router-link-active.nav-button > .nav-text {
    color: $active-color;
    font-weight: 600;
  }

  .router-link-active.nav-button > .nav-icon svg.px-icon {
    color: $active-color;
  }
}

.medium-screen-logo {
  display: none;
}

.sm-screen-hamburger {
  display: none;

  & > svg {
    width: 23px;
  }
}

.polly-nav-items {
  margin-bottom: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  list-style-type: none;
  padding: 0 0 0 0;

  .nested {
    display: flex;

    .bullet {
      height: 4px;
      margin: auto 0;
      margin-right: 8px;
      width: 4px;
    }
  }
}

.polly-logo {
  width: 100%;

  & > img {
    height: 14px;
  }
}

.nav-button-padding {
  margin: 0 0 0 0;
  padding: 8px 0 8px 0;
}

.medium-screen-text {
  display: none;
}

.parent-item > .sub-items {
  display: none;
}

.flex-nav-items > .polly-nav-items > .parent-item:only-child > .sub-items,
.active-parent-item > .sub-items,
.parent-item:focus-visible > .sub-items,
.parent-item:focus-within:not(:active) > .sub-items {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
  list-style-type: none;
}

.benchmark-padding {
  padding-bottom: 30px;
}

.separation-line {
  border: 1px solid #eeeeee;
  margin: 10px 20px 10px 0;
}

.navbar-hamburger {
  display: none;
}

@media (min-width: 1199px) {
  .polly-navbar {
    display: block !important;
  }

  .navbar-shadow {
    display: none !important;
  }
}

@media (max-width: 1399px) and (min-width: 776px) {
  .main {
    > .mai-wrapper {
      margin-left: 82px;
    }
  }

  .polly-navbar {
    display: block !important;
    box-shadow:
      inset 58px 0 5px 0 white,
      inset 0 0 6px 1px rgba(0, 0, 0, 0.25);

    & > .navbar-container {
      width: 82px;
      padding-left: 0;
    }

    & ~ .mai-wrapper {
      margin-left: 82px;
    }
  }

  .polly-nav-items .nested {
    padding-left: 24px;
  }

  .navbar-shadow {
    display: none !important;
  }

  .logo-row {
    width: 100%;
    justify-content: center;
  }

  .polly-logo,
  .sub-items {
    margin-left: 0 !important;
  }

  .polly-logo,
  .nav-button {
    display: flex;
    justify-content: center;
  }

  .nav-button > .nav-icon + .nav-text {
    display: none !important;
  }

  .nav-icon {
    margin-right: 0 !important;
  }

  .big-screen-text {
    display: none;
  }

  .medium-screen-text {
    display: block;
  }

  .big-screen-logo {
    display: none;
  }

  .user-orgs-accordion {
    span.ticker-link {
      display: inline-block;
    }

    .full-link {
      display: none;
    }

    div.btn-container,
    #userOrgsCollapse div {
      text-align: center;
    }

    .btn-container .default-org-btn {
      padding: 10px 5px;
    }

    #userOrgsCollapse ul {
      padding-left: 0;
      text-align: center;
    }

    li {
      list-style-type: none;
    }

    div a.more-user-orgs-link {
      padding-left: 0;
    }
  }

  .medium-screen-logo {
    display: block;
    width: 32px !important;
    height: 32px !important;
  }

  .separation-line {
    margin: 10px 10px 10px 10px;
  }

  .nav-icon.nested {
    width: 14px !important;
  }
}

@media (max-width: 776px) {
  .main {
    > .mai-wrapper {
      margin-left: 0;
    }
  }

  .polly-navbar {
    left: -196px;

    ~ .mai-wrapper {
      margin-left: 0;
    }
  }

  .navbar-shadow {
    display: block !important;
  }

  .navbar-hamburger {
    display: flex;
    z-index: 100;
    position: fixed;
    top: 20px;
    left: 24px;
    width: 43px;
    height: 43px;
    background-color: #f4f4f4;
    border-radius: 21px;
    justify-content: center;
    box-shadow: $polly-default-shadow;
    align-items: center;

    & > svg {
      display: flex;
      align-self: center;
      width: 23px;
    }
  }

  .sm-screen-hamburger {
    display: block;
    width: 100%;
    text-align: right;
    padding-right: 21px;
  }

  .nav-slide-in {
    display: block !important;
    animation: 0.3s nav-slide-in;
    left: 0;
  }

  .shadow-slide-in {
    animation: 0.3s shadow-slide-in;
    left: 194px;
  }

  .nav-slide-out {
    animation: 0.3s alternate nav-slide-out;
    left: -196px;
  }

  .shadow-slide-out {
    display: none !important;
  }

  @keyframes nav-slide-in {
    from {
      left: -196px;
    }

    to {
      left: 0;
    }
  }

  @keyframes shadow-slide-in {
    from {
      left: 0;
    }

    to {
      left: 194px;
    }
  }

  @keyframes nav-slide-out {
    from {
      left: 0;
    }

    to {
      left: -196px;
    }
  }
}

/*
User Orgs Accordion section
*/

.user-orgs-accordion {
  font-size: 0.8rem;

  span {
    font-size: 0.8rem;
  }

  .btn .icon {
    font-size: 1rem;
  }

  .btn-container {
    text-align: left;
  }

  .default-org-btn {
    display: flex;
    align-items: center;
    padding: 1rem 0;
    margin: 0;

    &.no-user-orgs {
      cursor: auto;
    }
  }

  .ticker-link {
    display: none;
  }

  ul {
    padding-left: 20px;
    margin-bottom: 0;
  }

  .more-user-orgs-link {
    padding-left: 5px;
  }

  #userOrgsCollapse li {
    padding: 2px 0;
  }
}

// regular disabled is not working so we need to use this
.disabled-link {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
